import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { IndianRupee, TrendingUp, Wallet, Receipt, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function Finance() {
  const [summary, setSummary] = useState(null);
  const [pnl, setPnl] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [trend, setTrend] = useState([]);
  const [gstTds, setGstTds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, c, t, g] = await Promise.all([
        api.get("/finance/summary"),
        api.get("/finance/claim-pnl?limit=50"),
        api.get("/finance/workshop-commissions"),
        api.get("/finance/monthly-trend"),
        api.get("/finance/gst-tds"),
      ]);
      setSummary(s.data);
      setPnl(p.data.rows || []);
      setCommissions(c.data.rows || []);
      setTrend(t.data.rows || []);
      setGstTds(g.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load finance data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !summary) return <div className="p-10 text-center text-slate-500" data-testid="finance-loading">Loading finance dashboard…</div>;
  if (err) return <div className="p-10 text-center text-rose-600" data-testid="finance-error">{err}</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 dark:bg-slate-950 py-8" data-testid="finance-page">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-600 dark:text-emerald-400 mb-1 flex items-center gap-2">
              <Wallet className="w-3 h-3" /> CFO Dashboard
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-slate-900 dark:text-white">Finance</h1>
            <p className="text-sm text-slate-500 mt-1">
              Rates · Commission {summary?.rates.commission_pct}% · GST {summary?.rates.gst_pct}% · TDS {summary?.rates.tds_pct}% · Fee ₹{summary?.rates.claim_fee_inr}
            </p>
          </div>
          <button onClick={load} className="text-xs border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-1.5 flex items-center gap-1.5 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-900" data-testid="finance-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>

        {/* Top KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi label="Net revenue MTD"    value={fmtINR(summary?.mtd.net_revenue_inr)} sub={`${summary?.mtd.settlements} settlements`} tone="emerald" testid="kpi-mtd" />
          <Kpi label="Net revenue YTD"    value={fmtINR(summary?.ytd.net_revenue_inr)} sub={`${summary?.ytd.settlements} settlements`} tone="blue" testid="kpi-ytd" />
          <Kpi label="Open receivables"   value={fmtINR(summary?.open_receivables_inr)} sub="in-flight claims" tone="orange" testid="kpi-open" />
          <Kpi label="Workshop payouts YTD" value={fmtINR(summary?.workshop_payouts_ytd_inr)} sub="paid out" tone="slate" testid="kpi-payouts" />
        </div>

        {/* Trend chart + GST/TDS side by side */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="finance-trend">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold text-slate-900 dark:text-white">Monthly revenue trend</div>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v, n) => [fmtINR(v), n]} />
                  <Line dataKey="gross_inr"       name="Gross"   stroke="#003DA5" strokeWidth={2} dot={false} />
                  <Line dataKey="net_revenue_inr" name="Net rev" stroke="#00A86B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="finance-gst-tds">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold text-slate-900 dark:text-white">GST · TDS · {gstTds?.year}</div>
              <Receipt className="w-4 h-4 text-orange-500" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div><div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">GST collected</div>
                <div className="font-heading font-black text-lg text-slate-900 dark:text-white">{fmtINR(gstTds?.totals.gst_collected_inr)}</div></div>
              <div><div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">TDS deducted</div>
                <div className="font-heading font-black text-lg text-slate-900 dark:text-white">{fmtINR(gstTds?.totals.tds_deducted_inr)}</div></div>
            </div>
            <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500 mb-1">Monthly</div>
            <div className="text-xs text-slate-600 dark:text-slate-300 space-y-1 max-h-40 overflow-y-auto">
              {(gstTds?.rows || []).map((r) => (
                <div key={r.month} className="flex justify-between border-b border-slate-100 dark:border-slate-800 py-1">
                  <span className="font-mono">{r.month}</span>
                  <span>GST {fmtINR(r.gst_collected_inr)} · TDS {fmtINR(r.tds_deducted_inr)}</span>
                </div>
              ))}
              {(gstTds?.rows || []).length === 0 && <div className="text-slate-400 text-xs">No settlements yet.</div>}
            </div>
          </div>
        </div>

        {/* Workshop commissions */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 mb-6 overflow-hidden" data-testid="finance-commissions">
          <div className="p-5 flex items-center justify-between">
            <div className="font-heading font-bold text-slate-900 dark:text-white">Workshop commissions & outstanding</div>
            <span className="text-xs text-slate-500">{commissions.length} workshops</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-950/50 text-slate-600 dark:text-slate-400">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Workshop</th>
                  <th className="text-right px-4 py-2 font-semibold">Settlements</th>
                  <th className="text-right px-4 py-2 font-semibold">Gross</th>
                  <th className="text-right px-4 py-2 font-semibold">Our commission</th>
                  <th className="text-right px-4 py-2 font-semibold">Paid</th>
                  <th className="text-right px-4 py-2 font-semibold">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {commissions.slice(0, 20).map((r) => (
                  <tr key={r.workshop_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`comm-row-${r.workshop_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.workshop_name || "—"}</div>
                      <div className="font-mono text-[10px] text-slate-500">{r.workshop_id}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{r.settlements}</td>
                    <td className="px-4 py-2 text-right">{fmtINR(r.gross_billed_inr)}</td>
                    <td className="px-4 py-2 text-right text-emerald-600 dark:text-emerald-400">{fmtINR(r.our_commission_inr)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{fmtINR(r.already_paid_inr)}</td>
                    <td className="px-4 py-2 text-right font-bold text-orange-600 dark:text-orange-400">{fmtINR(r.outstanding_inr)}</td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-8">No settled claims yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Claim P&L */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="finance-pnl">
          <div className="p-5 flex items-center justify-between">
            <div className="font-heading font-bold text-slate-900 dark:text-white">Claim-wise P&L</div>
            <span className="text-xs text-slate-500">{pnl.length} claims</span>
          </div>
          <div className="overflow-x-auto max-h-[500px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-950/50 text-slate-600 dark:text-slate-400 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Claim</th>
                  <th className="text-left px-4 py-2 font-semibold">Insurer</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-right px-4 py-2 font-semibold">Gross</th>
                  <th className="text-right px-4 py-2 font-semibold">Commission</th>
                  <th className="text-right px-4 py-2 font-semibold">Fee</th>
                  <th className="text-right px-4 py-2 font-semibold">GST</th>
                  <th className="text-right px-4 py-2 font-semibold">TDS</th>
                  <th className="text-right px-4 py-2 font-semibold">Net</th>
                </tr>
              </thead>
              <tbody>
                {pnl.map((r) => (
                  <tr key={r.claim_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`pnl-row-${r.claim_id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{r.claim_id}</td>
                    <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">{r.insurer || "—"}</td>
                    <td className="px-4 py-2 text-xs">{r.status}</td>
                    <td className="px-4 py-2 text-right">{fmtINR(r.gross_settled_inr)}</td>
                    <td className="px-4 py-2 text-right">{fmtINR(r.our_commission_inr)}</td>
                    <td className="px-4 py-2 text-right">{fmtINR(r.platform_fee_inr)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{fmtINR(r.gst_collected_inr)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{fmtINR(r.tds_deducted_inr)}</td>
                    <td className="px-4 py-2 text-right font-bold text-emerald-600 dark:text-emerald-400">{fmtINR(r.net_revenue_inr)}</td>
                  </tr>
                ))}
                {pnl.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-8">No claims yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = "blue", testid }) {
  const tones = {
    emerald: "ring-emerald-500/20",
    blue:    "ring-blue-500/20",
    orange:  "ring-orange-500/20",
    slate:   "ring-slate-500/10",
  };
  return (
    <div className={`rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 ring-1 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{label}</div>
        <IndianRupee className="w-3.5 h-3.5 text-slate-400" />
      </div>
      <div className="font-heading font-black text-2xl md:text-3xl text-slate-900 dark:text-white mt-1 truncate">{value}</div>
      <div className="text-xs text-slate-500">{sub}</div>
    </div>
  );
}
